// ─── BILLING UTILITIES ────────────────────────────────────────────────────────

// India GST on hotel accommodation: 5%
// (Budget hotels < ₹7500/night attract 12%, luxury > ₹7500 attract 18%,
//  but as per user configuration, default is 5%)
export const GST_RATE = 5;

export function calcNights(cin, cout) {
  if (!cin || !cout) return 1;
  const diff = new Date(cout) - new Date(cin);
  return Math.max(1, Math.round(diff / 86400000));
}

export function calcBill(reservation, billingType, customGstRate) {
  const { roomRate = 0, nights = 1, extraCharges = 0, damageCharges = 0, discount = 0, advancePaid = 0 } = reservation;
  
  const stayAmount = (Number(roomRate) * Number(nights)) - Number(discount);
  const taxableStay = Math.max(0, stayAmount);
  const otherCharges = Number(extraCharges) + Number(damageCharges);
  
  const isGst = billingType === 'gst';
  // User clarifies: GST should be applied on stay bill, not on service bill and Damage bill
  const appliedGstRate = isGst ? (customGstRate !== undefined && customGstRate !== null ? Number(customGstRate) : GST_RATE) : 0;
  
  // Round to paise (2 decimals), not whole rupees.
  const gstAmount = isGst ? Math.round(taxableStay * appliedGstRate / 100 * 100) / 100 : 0;

  const subtotal = taxableStay + otherCharges;
  const total = subtotal + gstAmount;
  const balanceDue = Math.max(0, total - advancePaid);
  
  return { 
    subtotal, 
    gstAmount, 
    gstRate: appliedGstRate, 
    total, 
    advancePaid, 
    balanceDue, 
    discount: Number(discount),
    taxableStay,
    otherCharges
  };
}

export const BILLING_TYPES = [
  { value: 'gst', label: 'Official GST Booking', series: 'GST', color: 'var(--teal)' },
  { value: 'non-gst', label: 'Simple / Non-GST Booking', series: 'NON', color: 'var(--muted)' },
];

export function fmt(n) {
  return `Rs. ${Number(n || 0).toLocaleString('en-IN')}`;
}

export function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function genId(prefix = 'ID') {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `${prefix}${ts}${rand}`;
}

export const PAYMENT_TYPES = [
  { value: 'cash', label: 'Cash', icon: '💵' },
  { value: 'upi', label: 'UPI', icon: '📱' },
  { value: 'credit_card', label: 'Credit Card', icon: '💳' },
  { value: 'debit_card', label: 'Debit Card', icon: '💳' },
  { value: 'cheque', label: 'Cheque', icon: '📝' },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
  // For a guest who arranges to settle directly with the owner outside the
  // hotel's own channels (e.g. a personal friend or relative of the owner) —
  // neither counts as cash/UPI/card actually collected by staff, so Cash
  // Management stays accurate to what's physically on hand. 'owner_upi' is
  // money that's already reached the owner (settled immediately); 'owner
  // credit' is an unpaid promise that stays pending until the owner reports
  // back what they actually collected.
  { value: 'owner_upi', label: 'Owner UPI', icon: '📲' },
  { value: 'owner_credit', label: 'Owner Credit', icon: '🤝' },
];

export const ROOM_TYPES = [
  'Standard', 'Deluxe', 'Deluxe King', 'Junior Suite', 'Suite',
  'Sea View', 'Pool View', 'Executive', 'Presidential Suite',
];

export const ID_TYPES = [
  'Aadhaar Card', 'Passport', 'Driving Licence', 'Voter ID',
  'PAN Card', 'Company ID', 'Foreign National ID',
];

export const EXPENSE_CATEGORIES = [
  'Housekeeping', 'Maintenance', 'Food & Beverage', 'Utilities',
  'Salaries', 'Marketing', 'Admin', 'Laundry', 'Security', 'Other',
];

export const NATIONALITIES = [
  'Indian', 'American', 'British', 'Australian', 'Canadian',
  'Chinese', 'Japanese', 'French', 'German', 'Other',
];

// toISOString() converts to UTC before slicing the date — between 12:00 AM and
// 5:29 AM IST that reports YESTERDAY's date, since IST is UTC+5:30. This app is
// used at exactly that hour (night-shift check-ins), so date math must stay in
// the browser's local time throughout instead of round-tripping through UTC.
function formatLocalDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function getTodayStr() {
  return formatLocalDate(new Date());
}

// A guest entry missed at the desk can be back-dated this many days, no further
// (today Oct 8 → earliest check-in Oct 6). The server enforces the same limit.
export const BACKDATE_DAYS = 2;

export function getMinBackdateStr() {
  const d = new Date();
  d.setDate(d.getDate() - BACKDATE_DAYS);
  return formatLocalDate(d);
}

export function getNextDayStr(dateStr) {
  // Parse "YYYY-MM-DD" as local time via the Date(y, m, d) constructor — the
  // Date(string) form parses date-only strings as UTC midnight, which shifts to
  // the previous calendar day once displayed back in a timezone ahead of UTC.
  const base = dateStr || getTodayStr();
  const [y, m, day] = base.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1, day || 1);
  d.setDate(d.getDate() + 1);
  return formatLocalDate(d);
}

export function getCurrentTimeStr() {
  const now = new Date();
  let hours = now.getHours();
  const minutes = now.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  return strTime;
}

/**
 * Securely validate image files using Magic Numbers (Binary Headers)
 * Supports PNG and JPG/JPEG
 */
export async function validateImageFile(file) {
  // 1. Check File Size (Max 5MB)
  const MAX_SIZE = 5 * 1024 * 1024;
  if (file.size > MAX_SIZE) {
    throw new Error(`File "${file.name}" is too large. Maximum size allowed is 5MB.`);
  }

  // 2. Check Extension/Mime (Primary check)
  const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
  if (!allowed.includes(file.type)) {
    throw new Error(`Invalid file type for "${file.name}". Only PNG, JPG, and PDF are allowed.`);
  }

  // 3. SECURE CHECK: Magic Numbers (Binary Header)
  // We read the first few bytes to verify it's actually an image
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = (e) => {
      const arr = new Uint8Array(e.target.result);
      let header = "";
      for (let i = 0; i < 4; i++) {
        header += arr[i].toString(16).padStart(2, '0').toUpperCase();
      }

      // PNG: 89504E47
      // JPG/JPEG: FFD8FF (starts with)
      // PDF: 25504446
      const isPNG = header === "89504E47";
      const isJPG = header.startsWith("FFD8FF");
      const isPDF = header === "25504446";

      if (isPNG || isJPG || isPDF) {
        resolve(true);
      } else {
        reject(new Error(`Security Alert: The file "${file.name}" is not a valid document despite its extension. Potential malware detected.`));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file.'));
    reader.readAsArrayBuffer(file.slice(0, 4));
  });
}

/**
 * Format phone to exactly 10 digits
 */
export function formatPhone(val) {
  return val.replace(/\D/g, '').substring(0, 10);
}

/**
 * Format ID number with dashes every 4 digits (up to 16 digits as requested)
 */
export function formatAadhaar(val) {
  const digits = (val || '').replace(/\D/g, '').substring(0, 12);
  // Format as xxxx-xxxx-xxxx
  if (digits.length <= 4) return digits;
  if (digits.length <= 8) return digits.slice(0, 4) + '-' + digits.slice(4);
  return digits.slice(0, 4) + '-' + digits.slice(4, 8) + '-' + digits.slice(8);
}

export function formatPAN(val) {
  return val.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10);
}

export function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
}

export function numToWords(amount) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convert(n) {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + convert(n % 100) : '');
    return '';
  }

  if (amount === 0) return 'Zero Rupees Only';
  let integral = Math.floor(amount);
  let words = '';

  if ( integral >= 10000000) {
    words += convert(Math.floor(integral / 10000000)) + ' Crore ';
    integral %= 10000000;
  }
  if (integral >= 100000) {
    words += convert(Math.floor(integral / 100000)) + ' Lakh ';
    integral %= 100000;
  }
  if (integral >= 1000) {
    words += convert(Math.floor(integral / 1000)) + ' Thousand ';
    integral %= 1000;
  }
  if (integral > 0) {
    words += convert(integral);
  }

  return 'Rupees ' + words.trim() + ' Only';
}


// Times are stored as "hh:mm AM/PM" across the app; <input type="time"> uses
// 24h "HH:MM". These convert between the two.
export function timeTo24h(time12) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(time12 || '');
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}

export function timeFrom24h(time24) {
  const [hStr, min] = (time24 || '').split(':');
  let h = parseInt(hStr, 10);
  if (isNaN(h) || min === undefined) return '';
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12; if (h === 0) h = 12;
  return `${String(h).padStart(2, '0')}:${min} ${ap}`;
}

// Late-checkout fee past the 12:00 PM cutoff on the checkout date. Lateness is
// measured up to when the guest actually left (checkout date + departureTime
// "HH:MM"), never past `now` — so a missed checkout entered the next day is
// billed for the hours the guest really stayed, not the hours until staff
// got to it.
export function calcLateCheckout({ checkOutDate, departureTime, now = new Date(), graceMins = 30, hourlyRate = 200, halfDayHours = 4, roomRate = 0 }) {
  const notLate = { isLate: false, fee: 0, hoursLate: 0 };
  if (!checkOutDate) return notLate;
  const parts = String(checkOutDate).split('T')[0].split('-').map(Number);
  if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) return notLate;

  const cutoff = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
  const [depH, depM] = (departureTime || '').split(':').map(Number);
  const departedAt = Number.isFinite(depH) && Number.isFinite(depM)
    ? new Date(parts[0], parts[1] - 1, parts[2], depH, depM, 0)
    : now;
  const leftAt = departedAt < now ? departedAt : now;

  if (leftAt.getTime() - (cutoff.getTime() + graceMins * 60000) <= 0) return notLate;

  const totalMinsLate = Math.ceil((leftAt.getTime() - cutoff.getTime()) / 60000);
  const hoursLate = Math.ceil(totalMinsLate / 60);
  const isHalfDay = hoursLate >= halfDayHours;
  const fee = isHalfDay
    ? (Math.round((roomRate / 2) / 100) * 100 || Math.round(roomRate / 2))
    : hoursLate * hourlyRate;
  return { isLate: true, hoursLate, totalMinsLate, fee, cutoffStr: '12:00 PM', isHalfDay };
}
